import { useCallback, useEffect, useState } from "react"

type StorageType = "localStorage" | "sessionStorage"
type SetValueAction<T> = T | ((value: T) => T)

const useStorage = <T = unknown>(key: string, initialValue?: T, options?: { storageType?: StorageType }) => {
    const storage: StorageType = options?.storageType || "localStorage"

    const readValue = (): T => {
        if (typeof window === "undefined") {
            return initialValue as T
        }

        const item = window[storage].getItem(key)
        try {
            return item ? (JSON.parse(item) as T) : (initialValue as T)
        } catch (error) {
            console.warn(`Error reading ${storage} key “${key}”:`, error)
            return (item as T) || (initialValue as T)
        }
    }

    const [storedValue, setStoredValue] = useState<T | null | undefined>(readValue)

    const setValue = useCallback(
        (value: SetValueAction<T | null | undefined>) => {
            if (typeof window === "undefined") {
                console.warn(`Tried setting ${storage} key “${key}” even though environment is not a client`)
                return
            }

            setStoredValue(prevValue => {
                try {
                    const newValue = value instanceof Function ? value(prevValue) : value

                    if (Object.is(prevValue, newValue)) return prevValue

                    if (newValue === undefined || newValue === null) {
                        window[storage].removeItem(key)
                    } else {
                        window[storage].setItem(key, JSON.stringify(newValue))
                    }

                    window.dispatchEvent(
                        new CustomEvent("onStorageChange", {
                            detail: { key, newValue },
                        })
                    )

                    return newValue
                } catch (error) {
                    console.warn(`Error setting ${storage} key “${key}”:`, error)
                    return prevValue
                }
            })
        },
        [key, storage]
    )

    useEffect(() => {
        const handleStorageChange = (event: Event) => {
            if (event.type === "onStorageChange") {
                const customEvent = event as CustomEvent<{ key: string; newValue: T }>
                if (customEvent.detail.key === key) {
                    setStoredValue(customEvent.detail.newValue)
                }
                return
            }

            const storageEvent = event as StorageEvent
            if (storageEvent.key === key) {
                setStoredValue(
                    storageEvent.newValue === null ? (null as T) : (JSON.parse(storageEvent.newValue) as T)
                )
            }
        }

        window.addEventListener("storage", handleStorageChange)
        window.addEventListener("onStorageChange", handleStorageChange)

        return () => {
            window.removeEventListener("storage", handleStorageChange)
            window.removeEventListener("onStorageChange", handleStorageChange)
        }
    }, [key])

    return [storedValue, setValue] as const
}

export { useStorage }
